// PlatformRails — admin-only page for managing platform_bank_accounts and
// the singleton platform_buckzy_credentials row.
//
// Phase 8 step 7. The platform is the operator's own funding side — admin
// pays merchants FROM these rails. List view + "Add platform rail" modal +
// "Configure Buckzy credentials" modal (one-time, singleton-enforced backend
// side; the panel shows the form regardless and surfaces the 409 cleanly).
//
// CLAUDE.md rule 4: this whole surface is gated on the real JWT role.
// RequireAuth role="admin" at the router level keeps non-admins out.
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { useUI } from "@/store/ui";
import { apiErrorMessage } from "@/utils/apiError";
import { providerLabel } from "@/utils/providerLabel";
import { usePlatformBankAccounts } from "@/queries/usePlatformBankAccounts";
import {
  createPlatformBankAccount,
  type PlatformProvider,
  type PlatformEnvironment,
} from "@/api/platformBankAccounts";
import { createPlatformBuckzyCredential } from "@/api/platformBuckzyCredentials";

export function PlatformRails() {
  const [showAddRail, setShowAddRail] = useState(false);
  const [showBuckzyCreds, setShowBuckzyCreds] = useState(false);
  const { data: rails = [], isLoading, isError } = usePlatformBankAccounts();

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Platform</div>
          <h1>Platform rails</h1>
          <div className="sub">
            The operator's own funding accounts. These rails are the SOURCE
            when admin pays out to merchants.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setShowBuckzyCreds(true)}
          >
            Configure Buckzy creds
          </button>
          <button
            type="button"
            className="btn accent"
            onClick={() => setShowAddRail(true)}
          >
            <I.Plus size={12} /> Add platform rail
          </button>
        </div>
      </div>

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>Loading rails…</div>
          </div>
        ) : isError ? (
          <div className="empty">
            <h3>Couldn't load platform rails</h3>
            <p>The request to payouts-backend failed. Try again shortly.</p>
          </div>
        ) : rails.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Banks size={16} />
            </div>
            <h3>No platform rails yet</h3>
            <p>Add an IDFC Bank or Buckzy rail to start paying merchants.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Label</th>
                <th>Provider</th>
                <th>Environment</th>
                <th>Status</th>
                <th>Created</th>
              </tr>
            </thead>
            <tbody>
              {rails.map((r) => (
                <tr key={r.platform_bank_account_id}>
                  <td>
                    <div className="cell-name">{r.label}</div>
                    <div className="cell-id">pba_{r.platform_bank_account_id}</div>
                  </td>
                  <td>{providerLabel(r.provider)}</td>
                  <td className="dim">{r.environment}</td>
                  <td>
                    <StatusPill status={r.disabled_at ? "disabled" : r.status} />
                  </td>
                  <td className="dim">{formatDateTime(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddPlatformRailModal
        open={showAddRail}
        onClose={() => setShowAddRail(false)}
      />
      <PlatformBuckzyCredsModal
        open={showBuckzyCreds}
        onClose={() => setShowBuckzyCreds(false)}
      />
    </div>
  );
}

// AUDIT-PASS-10 FE-L15: providerLabel lifted to @/utils/providerLabel so the
// merchant-side and platform-side pages share the same mapping.

// ─── Add platform rail modal ──────────────────────────────────────────────────

function AddPlatformRailModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const { addToast } = useUI();
  const [provider, setProvider] = useState<PlatformProvider>("paysmart_idfc");
  const [environment, setEnvironment] = useState<PlatformEnvironment>("sandbox");
  const [label, setLabel] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [merchantKey, setMerchantKey] = useState("");
  const [baseURL, setBaseURL] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setProvider("paysmart_idfc");
    setEnvironment("sandbox");
    setLabel("");
    setEmail("");
    setPassword("");
    setMerchantKey("");
    setBaseURL("");
    setSubmitting(false);
    setError(null);
    onClose();
  }

  // AUDIT-PASS-10 FE-M6: handleSubmit takes no event — both call sites
  // (form onSubmit, button onClick) handle their own preventDefault. The
  // earlier shape passed `new Event("submit") as unknown as FormEvent`
  // from the button, which is ugly and would silently break if the
  // handler ever touched e.currentTarget. Mirrors the Rbac.tsx pattern.
  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const payload =
        provider === "paysmart_idfc"
          ? { label, provider, environment, email, password, merchant_key: merchantKey, base_url: baseURL }
          : { label, provider, environment };
      const rail = await createPlatformBankAccount(payload);
      addToast({
        title: "Platform rail added",
        body: `${rail.label} · ${providerLabel(rail.provider)} (${rail.environment})`,
        variant: "success",
      });
      void qc.invalidateQueries({ queryKey: ["platform-bank-accounts"] });
      reset();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't create rail"));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={reset}
      title="Add platform rail"
      busy={submitting}
      foot={
        <>
          <button className="btn ghost" type="button" onClick={reset} disabled={submitting}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || !label}
          >
            {submitting ? "Creating…" : "Create rail"}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void handleSubmit();
        }}
        className="stack-16"
      >
        <div className="field">
          <label className="label" htmlFor="rail-provider">Provider</label>
          <select
            id="rail-provider"
            className="input"
            value={provider}
            onChange={(e) => setProvider(e.target.value as PlatformProvider)}
          >
            <option value="paysmart_idfc">IDFC Bank</option>
            <option value="buckzy_global_payout">Buckzy</option>
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="rail-env">Environment</label>
          <select
            id="rail-env"
            className="input"
            value={environment}
            onChange={(e) => setEnvironment(e.target.value as PlatformEnvironment)}
          >
            <option value="sandbox">Sandbox</option>
            <option value="production">Production</option>
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="rail-label">Label</label>
          <input
            id="rail-label"
            className="input"
            type="text"
            placeholder="Platform IDFC Sandbox"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
          />
        </div>

        {provider === "paysmart_idfc" ? (
          <>
            <div className="field">
              <label className="label" htmlFor="rail-email">Bank rail email</label>
              <input id="rail-email" className="input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="rail-password">Bank rail password</label>
              <input id="rail-password" className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="rail-mk">Merchant key</label>
              <input id="rail-mk" className="input" type="password" value={merchantKey} onChange={(e) => setMerchantKey(e.target.value)} />
            </div>
            <div className="field">
              <label className="label" htmlFor="rail-base">Base URL</label>
              <input
                id="rail-base"
                className="input"
                type="url"
                placeholder="https://apiprod.yajo.in/"
                value={baseURL}
                onChange={(e) => setBaseURL(e.target.value)}
              />
            </div>
          </>
        ) : (
          <div className="dim" style={{ fontSize: 12 }}>
            Buckzy rails use the platform-wide credential set (configured via
            the "Configure Buckzy creds" button above). No per-rail credentials
            needed here.
          </div>
        )}

        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </form>
    </Modal>
  );
}

// ─── Platform Buckzy creds modal ──────────────────────────────────────────────

// AUDIT-PASS-10 FE-H1: lifted to a const so reset() can restore EVERY field.
// Before this fix, reset() only cleared submitting and error — the credential
// secrets (client_secret, password, api_key) persisted in React state across
// modal close + reopen. CLAUDE.md rule 2 mandates access tokens never touch
// persistent storage; long-lived in-memory credential state is the next-tier
// risk (XSS, memory dumps). Now reset() restores the empty form on every
// close, including a successful save.
const INITIAL_BUCKZY_CREDS_FORM = {
  client_id: "",
  client_secret: "",
  api_key: "",
  username: "",
  password: "",
  api_url: "https://api-sbox.buckzy.net/",
  auth_url: "https://auth-sbox.buckzy.net/auth/oauth/",
  merchant_party_id: "",
  settlement_account_id: "",
};

function PlatformBuckzyCredsModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { addToast } = useUI();
  const [form, setForm] = useState(INITIAL_BUCKZY_CREDS_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }
  // AUDIT-PASS-10 FE-H1: clear secrets from React state on every close.
  function reset() {
    setForm(INITIAL_BUCKZY_CREDS_FORM);
    setSubmitting(false);
    setError(null);
    onClose();
  }

  // Backend requires every field — see CreatePlatformBuckzyCredentialPayload.
  // Pre-fix, merchant_party_id + settlement_account_id were optional in the
  // TS type and stripped to `undefined` here; the backend's `binding:"required"`
  // (AUDIT-PASS-10 BE-H5) then bounced the request as a generic 400
  // invalid_request. Now: required at the type, gated at the button, sent
  // verbatim.
  const canSubmit =
    !submitting &&
    form.client_id.trim() !== "" &&
    form.client_secret !== "" &&
    form.api_key !== "" &&
    form.username.trim() !== "" &&
    form.password !== "" &&
    form.api_url.trim() !== "" &&
    form.auth_url.trim() !== "" &&
    form.merchant_party_id.trim() !== "" &&
    form.settlement_account_id.trim() !== "";

  async function handleSubmit() {
    setError(null);
    setSubmitting(true);
    try {
      const result = await createPlatformBuckzyCredential(form);
      addToast({
        title: "Platform Buckzy credentials saved",
        body: `cred id: ${result.platform_buckzy_credential_id}`,
        variant: "success",
      });
      reset();
    } catch (err) {
      setError(apiErrorMessage(err, "Couldn't save Buckzy creds"));
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={reset}
      title="Configure platform Buckzy credentials"
      busy={submitting}
      foot={
        <>
          <button className="btn ghost" type="button" onClick={reset} disabled={submitting}>
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!canSubmit}
          >
            {submitting ? "Saving…" : "Save credentials"}
          </button>
        </>
      }
    >
      <div className="stack-16">
        <div className="dim" style={{ fontSize: 12 }}>
          Singleton: only one active platform-Buckzy credential set exists at
          a time. A second save returns 409 — rotate via a future endpoint.
        </div>
        <div className="dim" style={{ fontSize: 12, lineHeight: 1.5 }}>
          <strong>About the last two fields:</strong> on this screen they
          refer to <em>the platform's own</em> Buckzy identity — NOT any
          single merchant's. <strong>Platform party ID</strong> is the
          Buckzy <code>partyId</code> issued for the platform/distributor
          account; it lands as <code>debtor.reference</code> on outbound
          payouts to merchants. <strong>Platform settlement account ID</strong> is
          the Buckzy account reference Buckzy will debit when payouts settle.
          Both come from Buckzy when the platform-tier account is registered.
        </div>
        {(
          [
            ["client_id", "Client ID"],
            ["client_secret", "Client secret"],
            ["api_key", "API key"],
            ["username", "Username"],
            ["password", "Password"],
            ["api_url", "API URL (https://…)"],
            ["auth_url", "Auth URL (https://…)"],
            // The JSON key is `merchant_party_id` because the same column shape
            // is reused on the merchant tier. On THIS screen the value is the
            // PLATFORM's own Buckzy partyId — i.e. the distributor identity
            // that becomes `debtor.reference` on outbound payouts to merchants.
            // Label is rewritten so admins don't think they're entering some
            // single merchant's id (CTL note from screen-side audit).
            ["merchant_party_id", "Platform party ID (Buckzy debtor)"],
            ["settlement_account_id", "Platform settlement account ID"],
          ] as const
        ).map(([k, label]) => (
          <div className="field" key={k}>
            <label className="label" htmlFor={`pbc-${k}`}>{label}</label>
            <input
              id={`pbc-${k}`}
              className="input"
              type={k.includes("password") || k.includes("secret") || k === "api_key" ? "password" : "text"}
              value={form[k]}
              onChange={(e) => update(k, e.target.value)}
            />
          </div>
        ))}
        {error && (
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </div>
    </Modal>
  );
}
